import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { MERCHANT_ROUTES, ProtectedRoute, ToastContainer, UserRole } from '@dukene/shared';
import { ConfirmModalContainer } from './components/modals/ConfirmModal';

// Layouts
import DashboardLayout from './components/layout/DashboardLayout';
import AuthLayout from './components/layout/AuthLayout';
import SettingsLayout from './components/layout/SettingsLayout';
import StoreLayout from './components/layout/StoreLayout';

// Auth Pages
import Login from './pages/auth/Login';
import ForgotPassword from './pages/auth/ForgotPassword';
import Register from './pages/auth/Register';
import VerifyEmail from './pages/auth/VerifyEmail';
import AcceptInvitation from './pages/auth/AcceptInvitation';
import ResetPassword from './pages/auth/ResetPassword';
import SelectStore from './pages/auth/SelectStore';

// Dashboard Pages
import Dashboard from './pages/Dashboard';
import Products from './pages/products/Products';
import ProductNewEnhanced from './pages/products/ProductNewEnhanced';
import ProductEdit from './pages/products/ProductEdit';
import Categories from './pages/Categories';
import CategoryDetail from './pages/CategoryDetail';
import Inventory from './pages/Inventory';
import Orders from './pages/orders/Orders';
import OrderDetail from './pages/orders/OrderDetail';
import Customers from './pages/customers/Customers';
import CustomerDetail from './pages/customers/CustomerDetail';
import Analytics from './pages/Analytics';
import Settings from './pages/settings/Settings';
import StoreSettings from './pages/settings/StoreSettings';
import PaymentSettings from './pages/settings/PaymentSettings';
import PricingSettings from './pages/settings/PricingSettings';
import ShippingSettings from './pages/settings/ShippingSettings';
import WhatsAppConfiguration from './pages/whatsapp/WhatsAppConfiguration';
import WhatsAppConversations from './pages/whatsapp/WhatsAppConversations';
import WhatsAppTemplates from './pages/whatsapp/WhatsAppTemplates';
import BotFlowsList from './pages/whatsapp/BotFlowsList';
import BotFlowBuilder from './pages/whatsapp/BotFlowBuilder';
import PoliciesSettings from './pages/settings/PoliciesSettings';
import Profile from './pages/Profile';
import Discounts from './pages/discounts/Discounts';
import DiscountForm from './pages/discounts/DiscountForm';
import Notifications from './pages/Notifications';
import Suppliers from './pages/suppliers/Suppliers';
import SupplierDetail from './pages/suppliers/SupplierDetail';
import SupplierForm from './pages/suppliers/SupplierForm';
import Marketplace from './pages/suppliers/Marketplace';
import MarketplaceSupplierDetail from './pages/suppliers/MarketplaceSupplierDetail';
import ConnectedSuppliers from './pages/suppliers/ConnectedSuppliers';
import SupplierSettings from './pages/settings/SupplierSettings';
import BillingSettings from './pages/settings/BillingSettings';
import CreditsWallet from './pages/settings/CreditsWallet';
import Reviews from './pages/Reviews';
import MetaCommerce from './pages/MetaCommerce';
import WhatsAppCatalog from './pages/WhatsAppCatalog';
import GoogleCommercePage from './pages/GoogleCommercePage';
import SalesChannels from './pages/SalesChannels';
import PointOfSalePage from './pages/PointOfSalePage';
import BuyButtonPage from './pages/BuyButtonPage';
import OrderRoutes from './pages/shipping/OrderRoutes';
import StoreManagers from './pages/settings/StoreManagers';
import NewStore from './pages/settings/NewStore';
import ThemeManager from './pages/settings/themes/ThemeManager';
import VisualBuilder from './pages/settings/themes/VisualBuilder';
import CodeEditor from './pages/settings/themes/CodeEditor';
import Pages from './pages/online-store/Pages';
import PageEditor from './pages/online-store/PageEditor';
import Navigation from './pages/online-store/Navigation';
import ThemeMarketplace from './pages/online-store/ThemeMarketplace';
import MenuBuilder from './pages/online-store/MenuBuilder';
import PaymentLinksList from './pages/payment-links/PaymentLinksList';
import CreatePaymentLink from './pages/payment-links/CreatePaymentLink';
import NotFound from './pages/NotFound';

function App() {
  return (
    <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <ToastContainer />
      <Routes>
          {/* Auth Routes */}
          <Route element={<AuthLayout />}>
            <Route path="/login" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/register" element={<Register />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/accept-invitation" element={<AcceptInvitation />} />
            <Route path="/select-store" element={<SelectStore />} />
          </Route>

          {/* Dashboard Routes - Protected */}
          <Route element={<ProtectedRoute allowedRoles={[UserRole.MERCHANT, UserRole.OWNER, UserRole.STAFF, UserRole.CO_OWNER]}><DashboardLayout /></ProtectedRoute>}>
            <Route path={MERCHANT_ROUTES.DASHBOARD} element={<Dashboard />} />
            <Route path={MERCHANT_ROUTES.PRODUCTS} element={<Products />} />
            <Route path={MERCHANT_ROUTES.PRODUCTS_NEW} element={<ProductNewEnhanced />} />
            <Route path={MERCHANT_ROUTES.PRODUCTS_EDIT} element={<ProductEdit />} />
            <Route path={MERCHANT_ROUTES.CATEGORIES} element={<Categories />} />
            <Route path={MERCHANT_ROUTES.CATEGORIES_DETAIL} element={<CategoryDetail />} />
            <Route path={MERCHANT_ROUTES.INVENTORY} element={<Inventory />} />
            <Route path="/products/reviews" element={<Reviews />} />
            <Route path={MERCHANT_ROUTES.ORDERS} element={<Orders />} />
            <Route path={MERCHANT_ROUTES.ORDERS_DETAIL} element={<OrderDetail />} />
            <Route path={MERCHANT_ROUTES.CUSTOMERS} element={<Customers />} />
            <Route path={MERCHANT_ROUTES.CUSTOMERS_DETAIL} element={<CustomerDetail />} />
            <Route path={MERCHANT_ROUTES.ANALYTICS} element={<Analytics />} />

            {/* Settings with Sub-sidebar Layout */}
            <Route element={<SettingsLayout />}>
              <Route path={MERCHANT_ROUTES.SETTINGS} element={<Settings />} />
              <Route path="/settings/billing" element={<BillingSettings />} />
              <Route path="/settings/wallet" element={<CreditsWallet />} />
              <Route path="/billing/wallet" element={<CreditsWallet />} />
              <Route path={MERCHANT_ROUTES.SETTINGS_PAYMENTS} element={<PaymentSettings />} />
              <Route path="/settings/pricing" element={<PricingSettings />} />
              <Route path={MERCHANT_ROUTES.SETTINGS_SHIPPING} element={<ShippingSettings />} />
              <Route path="/settings/suppliers" element={<SupplierSettings />} />
              <Route path="/settings/managers" element={<StoreManagers />} />
              <Route path={MERCHANT_ROUTES.SETTINGS_STORES_NEW} element={<NewStore />} />
            </Route>
            {/* Online Store with Sub-sidebar Layout */}
            <Route element={<StoreLayout />}>
              <Route path="/store" element={<StoreSettings />} />
              <Route path="/store/design" element={<StoreSettings />} />
              <Route path="/store/domains" element={<StoreSettings />} />
              <Route path="/store/seo" element={<StoreSettings />} />
              <Route path="/store/features" element={<StoreSettings />} />
              <Route path="/store/whatsapp" element={<WhatsAppConfiguration />} />
              <Route path="/store/policies" element={<PoliciesSettings />} />
              <Route path="/store/themes" element={<ThemeManager />} />
              <Route path="/store/themes/marketplace" element={<ThemeMarketplace />} />
              <Route path="/store/pages" element={<Pages />} />
              <Route path="/store/pages/:id" element={<PageEditor />} />
              <Route path="/store/navigation" element={<Navigation />} />
              <Route path="/store/navigation/:id" element={<MenuBuilder />} />
            </Route>
            {/* Theme Builders */}
            <Route path="/store/themes/:id/editor" element={<VisualBuilder />} />
            <Route path="/store/themes/:id/code" element={<CodeEditor />} />

            <Route path={MERCHANT_ROUTES.PROFILE} element={<Profile />} />
            <Route path="/discounts" element={<Discounts />} />
            <Route path="/discounts/new" element={<DiscountForm />} />
            <Route path="/discounts/:id/edit" element={<DiscountForm />} />
            <Route path={MERCHANT_ROUTES.NOTIFICATIONS} element={<Notifications />} />
            <Route path={MERCHANT_ROUTES.BILLING} element={<Navigate to="/settings/billing" replace />} />
            <Route path="/whatsapp" element={<Navigate to="/whatsapp/configuration" replace />} />
            <Route path="/whatsapp/configuration" element={<Navigate to="/store/whatsapp" replace />} />
            <Route path="/whatsapp/conversations" element={<WhatsAppConversations />} />
            <Route path="/whatsapp/templates" element={<WhatsAppTemplates />} />
            <Route path="/whatsapp/flows" element={<BotFlowsList />} />
            <Route path="/whatsapp/flows/:id" element={<BotFlowBuilder />} />
            <Route path="/suppliers" element={<Suppliers />} />
            <Route path="/suppliers/connected" element={<ConnectedSuppliers />} />
            <Route path="/suppliers/marketplace" element={<Marketplace />} />
            <Route path="/suppliers/marketplace/:supplierId" element={<MarketplaceSupplierDetail />} />
            <Route path="/suppliers/new" element={<SupplierForm />} />
            <Route path="/suppliers/:id" element={<SupplierDetail />} />
            <Route path={MERCHANT_ROUTES.SALES_CHANNELS} element={<SalesChannels />} />
            <Route path={MERCHANT_ROUTES.META_COMMERCE} element={<MetaCommerce />} />
            <Route path={MERCHANT_ROUTES.GOOGLE_COMMERCE} element={<GoogleCommercePage />} />
            <Route path={MERCHANT_ROUTES.WHATSAPP_CATALOG} element={<WhatsAppCatalog />} />
            <Route path="/pos" element={<PointOfSalePage />} />
            <Route path="/buy-button" element={<BuyButtonPage />} />
            <Route path={MERCHANT_ROUTES.AUTOMATIONS} element={<Navigate to={MERCHANT_ROUTES.META_COMMERCE} replace />} />
            <Route path={MERCHANT_ROUTES.CHECKOUT_LINKS} element={<PaymentLinksList />} />
            <Route path={`${MERCHANT_ROUTES.CHECKOUT_LINKS}/new`} element={<CreatePaymentLink />} />
            <Route path="/shipping/routes" element={<OrderRoutes />} />
          </Route>

          {/* 404 Page */}
          <Route path="*" element={<NotFound />} />
        </Routes>
        <ConfirmModalContainer />
    </BrowserRouter>
  );
}

export default App;
